import * as React from 'react'
import { connect } from 'react-redux'
import * as _ from 'underscore'
import { TidewaysContext } from './app/context'
import Navigator from './app/navigator'
import { Report } from './domain/Report'
import Link from './helper/link'
import Milliseconds from './helper/milliseconds'
import { RangeSelectorInterface } from './history/RangeSelectorInterface'
import { ApplicationState } from './redux/ApplicationState'

interface OwnProps {
    data?: Report<any> | null
    selector: RangeSelectorInterface
    showGraph: boolean
    allServices: boolean
    children: React.ReactNode
}

interface StateProps {
    tidewaysContext: TidewaysContext
    navigator: Navigator
}

type Props = StateProps & OwnProps

interface State {
    compactServices: boolean
    compactEnvironments: boolean
}

interface Nodes {
    leftCol?: HTMLElement
    service?: HTMLElement
    environment?: HTMLElement
}

export class ServiceEnvironmentNavigationComponent extends React.Component<Props, State> {
    public static defaultProps = {
        showGraph: true,
    }

    public state: State = {
        compactServices: false,
        compactEnvironments: false,
    }

    private nodes: Nodes = {}

    public render() {
        const ALL_SERVICES = '__all'

        return (
            <div className="row">
                <div className="col-lg-12" ref={element => this.addDomNode('leftCol', element)}>
                    <ul
                        className="nav nav-pills"
                        ref={element => this.addDomNode('service', element)}
                        style={{ float: 'left', marginRight: '2em', marginBottom: '0' }}
                        role="menu"
                    >
                        <li className="nav-label">
                            <span>
                                Service{' '}
                                <Link route="XhprofProfilerBundle.Service.list">
                                    <i className="fa fa-cog text-muted" />
                                </Link>
                            </span>
                        </li>
                        {this.state.compactServices
                            ? this.renderServiceDropdown(this.getServicesList(), this.props.tidewaysContext.getService())
                            : this.renderServiceList(this.getServicesList(), this.props.tidewaysContext.getService())}
                        {this.props.allServices ? (
                            <li
                                key={ALL_SERVICES}
                                className={this.props.tidewaysContext.getService() === ALL_SERVICES ? 'active' : undefined}
                            >
                                <a className="nav-link" onClick={() => this.props.selector.setService(ALL_SERVICES)}>
                                    All&nbsp;
                                </a>
                            </li>
                        ) : null}
                    </ul>
                    <ul
                        className="nav nav-pills"
                        ref={element => this.addDomNode('environment', element)}
                        style={{ float: 'left', marginBottom: '0' }}
                        role="menu"
                    >
                        <li className="nav-label">
                            <span>
                                Environment{' '}
                                <Link route="XhprofProfilerBundle.Environment.list">
                                    <i className="fa fa-cog text-muted" />
                                </Link>
                            </span>
                        </li>
                        {this.state.compactEnvironments
                            ? this.renderEnvironmentDropdown(
                                  this.props.tidewaysContext.environments,
                                  this.props.tidewaysContext.getEnvironment(),
                              )
                            : this.renderEnvironmentList(
                                  this.props.tidewaysContext.environments,
                                  this.props.tidewaysContext.getEnvironment(),
                              )}
                    </ul>
                    {this.props.tidewaysContext.application?.showLimitsReached ? (
                        <ul className="nav nav-pills" style={{ float: 'left', marginBottom: '0', marginLeft: '2em' }} role="menu">
                            <li>
                                <Link route="XhprofOrganizationBundle.Application.license">
                                    <i className="fa fa-warning" /> Limits Reached
                                </Link>
                            </li>
                        </ul>
                    ) : null}
                    {this.props.tidewaysContext.application?.updateRecommended &&
                    !this.props.tidewaysContext.application?.showLimitsReached ? (
                        <ul className="nav nav-pills" style={{ float: 'left', marginBottom: '0', marginLeft: '2em' }} role="menu">
                            <li>
                                <Link route="XhprofProfilerBundle.Server.list">
                                    <i className="fa fa-warning" /> Outdated Versions
                                </Link>
                            </li>
                        </ul>
                    ) : null}

                    <div>{this.props.children}</div>
                </div>
            </div>
        )
    }

    private getServicesList = () =>
        this.props.selector.supportsNonDefaultServices()
            ? this.props.tidewaysContext.services.serviceIds
            : {
                  [this.props.tidewaysContext.application.defaultService]:
                      this.props.tidewaysContext.services.serviceIds[this.props.tidewaysContext.application.defaultService] || 0,
              }

    private renderEnvironmentList = (items, active) =>
        _.map(items, name => (
            <li key={name} className={name === active ? 'active' : undefined}>
                <a className="nav-link" onClick={() => this.props.selector.setEnvironment(name)}>
                    {name}
                </a>
            </li>
        ))

    private renderEnvironmentDropdown = (items, active) => (
        <li className="dropdown">
            <a className="dropdown-toggle" data-toggle="dropdown" href="#">
                {active} <span className="caret" />
            </a>
            <ul className="dropdown-menu">{this.renderEnvironmentList(items, active)}</ul>
        </li>
    )

    private renderServiceList = (items, active) =>
        _.map(items, (id, name) => {
            return (
                <li key={name} className={name === active ? 'active' : undefined}>
                    <a className="nav-link" onClick={() => this.props.selector.setService(name)}>
                        {name}&nbsp;
                        {this.props.showGraph && this.props.data ? (
                            <Milliseconds value={this.props.data.slices[name] ? this.props.data.slices[name].responseTime * 1000 : 0} />
                        ) : null}
                    </a>
                </li>
            )
        })

    private renderServiceDropdown = (items, active) => (
        <li className="dropdown">
            <a className="dropdown-toggle" data-toggle="dropdown" href="#">
                {active}&nbsp;
                {this.props.showGraph && this.props.data ? (
                    <Milliseconds value={this.props.data.slices[active] ? this.props.data.slices[active].responseTime * 1000 : 0} />
                ) : null}
                <span className="caret" />
            </a>
            <ul className="dropdown-menu">{this.renderServiceList(items, active)}</ul>
        </li>
    )

    private addDomNode = (key, element) => {
        if (!element) {
            return
        }

        this.nodes[key] = element

        this.checkForLayoutWrap()
    }

    private checkForLayoutWrap = () => {
        if (!(this.nodes.leftCol && this.nodes.service && this.nodes.environment)) {
            return
        }

        if (this.state.compactEnvironments && this.state.compactServices) {
            return
        }

        const leftCol = this.nodes.leftCol.getBoundingClientRect().height
        const environment = this.nodes.environment.getBoundingClientRect().height
        const service = this.nodes.service.getBoundingClientRect().height

        // check if leftCol got wrapped in 2 lines
        if (leftCol < environment + service) {
            return
        }

        if (this.state.compactEnvironments && this.props.tidewaysContext.environments.length > 0) {
            this.setState({ compactServices: true })
        } else if (this.props.tidewaysContext.environments.length === 0) {
            this.setState({ compactServices: true, compactEnvironments: true })
        } else {
            this.setState({ compactEnvironments: true })
        }
    }
}

export default connect<StateProps>((globalState: ApplicationState, props: OwnProps) => {
    return {
        tidewaysContext: globalState.tidewaysContext,
        navigator: new Navigator(globalState.tidewaysContext.route),
        ...props,
    }
})(ServiceEnvironmentNavigationComponent)
